import React from "react";
import Button from "../../modules/components/Button";
import { Add } from '@mui/icons-material';
import NewWatchForm from '../../modules/views/NewWatchForm';
import { Container, Link } from "@mui/material";
import SubsciptionCard from "../../modules/components/SubscriptionCard";
import { 
  deleteUserSubscription, 
  getUserSubscriptions, 
  refreshUserSubscription, 
  updateUserSubscriptionNotifyEmail,
  updateUserSubscriptionNotifyPhone } from "../../modules/components/api";
import VideoModal from "../../modules/components/VideoModal";
import SubscriptionsWelcome from '../../modules/views/SubscriptionsWelcome';
import { useOutletContext } from "react-router-dom";
import Snackbar from '../../modules/components/Snackbar';
import logger from "../../modules/components/logger";

const UpgradeMessage = () => (
  <>
    <Link href="/dashboard/upgrade">
      Upgrade to Collector
    </Link>
    , and unlock phone notifications plus the ability to add more watch subscriptions.
  </>
);

const NoPhoneMessage = () => (
  <>
    {"No validated phone number found. "}
    <Link href="/dashboard/update-profile">
      Update your Contact Info
    </Link>
    , and then try again!
  </>
);

export default function Subscriptions() {
  const [refresh, setRefresh] = React.useState(0);
  const [userWatchSubscriptions, setUserWatchSubscriptions] = React.useState([]);
  const [adding, setAdding] = React.useState(false);
  const inputRef = React.useRef(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [userInfo,,] = useOutletContext();
  const [open, setOpen] = React.useState(false);
  const [openNoPhone, setOpenNoPhone] = React.useState(false);

  React.useEffect(() => {
    if (isLimitExceeded())
        setAdding(false);
    if (adding)
      inputRef.current.focus();
  }, [adding, userWatchSubscriptions]);

  React.useEffect(() => {
    if (!isEmpty(userInfo) && refresh !== 1)
      getUserSubscriptions(userInfo.id)
        .then((res) => {
          setUserWatchSubscriptions(res);
          if (refresh === 0)
            setRefresh(1);
        }).catch(logger.error);
  }, [refresh, userInfo]);

  const handleClickAddWatch = () => {
    if (isLimitExceeded()) {
      setOpen(true);
    }
    else {
      setAdding(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  }

  const handleClickCancel = () => {
    setAdding(false);
  };

  const handleClickDelete = (id) => {
    deleteUserSubscription(id).then(() => {
      setRefresh(prevRefresh => ++prevRefresh);
    });
  }

  const handleClickRefresh = (id) => {
    const expiration = new Date();
    expiration.setDate(expiration.getDate() + 15);
    refreshUserSubscription(id, expiration.getTime().toString().slice(0, -3)).then(() => {
      setRefresh(prevRefresh => ++prevRefresh);
    });
  }

  const handleClickNotifyEmail = (id, notifyEmail) => {
    updateUserSubscriptionNotifyEmail(id, !notifyEmail).then(() => {
      setRefresh(prevRefresh => ++prevRefresh);
    });
  }

  const handleClickNotifyPhone = (id, notifyPhone) => {
    if (isLimitExceeded() || !userInfo?.collector) {
      setOpen(true);
    }
    else {
      if (userInfo.phone && userInfo.phoneValidated) {
        updateUserSubscriptionNotifyPhone(id, !notifyPhone).then(() => {
          setRefresh(prevRefresh => ++prevRefresh);
        });
      }
      else {
        setOpenNoPhone(true);
      }
    }
  }

  const handleCloseNoPhone = () => {
    setOpenNoPhone(false);
  }

  // sorts alphabetically by brand, then by description
  const comparator = (a, b) => {
    // return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
    if (a.brand.toLowerCase() === b.brand.toLowerCase())
      return a.description.localeCompare(b.description);
    else
      return a.brand.localeCompare(b.brand);
  };

  const isEmpty = object => Object.entries(object).length === 0;

  const limit = 2;
  const collectorLimit = 6;
  const isLimitExceeded = () => userInfo?.collector ? 
    userWatchSubscriptions?.length >= collectorLimit : userWatchSubscriptions?.length >= limit;

  return (
    <React.Fragment>
      <Container maxWidth="sm" sx={{ flexGrow: 1, p: 2 }}>
        <div>
          {userWatchSubscriptions?.length !== 0 && userWatchSubscriptions?.sort(comparator)?.map((data, index) => (
            <SubsciptionCard 
              userInfo={userInfo}
              key={"subscription" + index} 
              data={data} 
              index={index} 
              handleClickDelete={handleClickDelete} 
              handleClickRefresh={handleClickRefresh}
              handleClickNotifyEmail={handleClickNotifyEmail}
              handleClickNotifyPhone={handleClickNotifyPhone}
              disabled={!userInfo?.collector && index >= limit}
            />
          ))}
          {refresh > 0 && (userWatchSubscriptions?.length === 0 || !userWatchSubscriptions) && 
            <SubscriptionsWelcome setModalOpen={setModalOpen} />
          }
        </div>
        {refresh > 0 && 
          (adding ?
            <NewWatchForm
              userWatchSubscriptions={userWatchSubscriptions}
              handleClickCancel={handleClickCancel} 
              setRefresh={setRefresh} 
              inputRef={inputRef} 
              userInfo={userInfo} 
            />
            :
            <Button 
              variant="outlined" 
              startIcon={<Add />} 
              sx={refresh > 0 ? { m: 3, mt: 0 } : { m: 3 }} 
              onClick={handleClickAddWatch}
            >
              Add Watch
            </Button>
          )
        }
      </Container>
      <VideoModal
        open={modalOpen}
        setOpen={setModalOpen}
      />
      <Snackbar
        open={open}
        closeFunc={handleClose}
        message={!userInfo?.collector ? <UpgradeMessage /> : `Limit for collectors is ${collectorLimit} watch subscriptions.`}
      />
      <Snackbar
        open={openNoPhone}
        closeFunc={handleCloseNoPhone}
        message={<NoPhoneMessage />}
      />
    </React.Fragment>
  );
}